import React, { useEffect, useState } from "react";
import { ReactComponent as NewDesignIcon } from "../../../../../../assets/svg/NewDesignIcon.svg";
import { ReactComponent as NewRenovationIcon } from "../../../../../../assets/svg/NewRenovationIcon.svg";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import moment from "moment";
import { Setting } from "../../../../../../utils/Setting";
import { getApiData } from "../../../../../../utils/APIHelper";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import { ReactComponent as MwebDhiramIcon } from "../../../../../../assets/svg/MwebDhiramIcon.svg";

/** Card shell: grey title bar + white body (Project timeline + both tables) */
const impactCardSx = {
  border: "1px solid #E4E5E7",
  borderRadius: "8px",
  overflow: "hidden",
  mb: 3,
  backgroundColor: "#fff",
};

const impactCardTitleSx = {
  backgroundColor: "#f3f4f6",
  px: 2,
  py: 1.25,
  borderBottom: "1px solid #E4E5E7",
};

const impactCardTitleTextSx = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#374151",
};

const timelineCardsPaddingSx = {
  p: 2,
  backgroundColor: "#fff",
};

/** Shared table chrome for Milestones & Contractor Payments */
const impactTablePaperSx = {
  boxShadow: "none",
  border: "none",
  borderRadius: 0,
  backgroundColor: "#fff",
};

const impactTableHeadRowSx = {
  backgroundColor: "#fff",
  "& .MuiTableCell-root": {
    borderBottom: "none",
    py: 1.5,
    px: 2,
    fontSize: "12px",
    fontWeight: 600,
    color: "#949DAA",
  },
};

const impactTableBodyCellSx = {
  fontSize: "14px",
  color: "#0d0d0e",
  py: 2,
  px: 2,
  borderBottom: "1px solid #f3f4f6",
  verticalAlign: "middle",
};

function sameCalendarDay(a, b) {
  if (a == null || b == null) return true;
  return moment(a).startOf("day").isSame(moment(b).startOf("day"));
}

function computeProjectNetChangeDays(project) {
  if (!project) return 0;
  const { originalEndDate, proposedEndDate, originalStartDate, proposedStartDate } = project;
  if (originalEndDate && proposedEndDate) {
    const endDiff = moment(proposedEndDate).startOf("day").diff(moment(originalEndDate).startOf("day"), "days");
    if (endDiff !== 0) return endDiff;
  }
  if (originalStartDate && proposedStartDate) {
    return moment(proposedStartDate).startOf("day").diff(moment(originalStartDate).startOf("day"), "days");
  }
  return 0;
}

/** Contractor / milestone impact column: "9 days" or "No change" (no + prefix). */
function formatPaymentImpactDays(days) {
  if (days === 0) return "No change";
  return `${Math.abs(days)} days`;
}

function formatNetChangeLabel(days) {
  if (days === 0) return "0 days";
  if (days > 0) return `+${days} days`;
  return `${days} days`;
}

function TimelineStatCard({ value, label, valueColor = "#0d0d0e", paperSx = {} }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid #E4E5E7",
        borderRadius: "8px",
        flex: 1,
        minWidth: { xs: "100%", sm: "140px" },
        boxSizing: "border-box",
        ...paperSx,
      }}
    >
      <Typography sx={{ fontSize: "16px", fontWeight: 700, color: valueColor, mb: 0.5, lineHeight: 1.3 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: "12px", color: "#949DAA", fontWeight: 500 }}>{label}</Typography>
    </Paper>
  );
}

const TimelineImpactReviewCR = () => {
  const [searchQuery] = useSearchParams();
  const changeId = searchQuery.get("change");

  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (changeId) {
      fetchPreview();
    }
  }, [changeId]);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      const res = await getApiData(
        Setting.endpoints.changeRequest.preview(changeId),
        "GET",
        {},
        {},
        true
      );

      if (res?.project) {
        setPreviewData(res);
      } else if (res?.data?.project) {
        setPreviewData(res.data);
      }
    } catch (err) {
      toast.error(err.message || "Failed to fetch impact preview");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography sx={{ fontSize: "14px", color: "#666" }}>Loading...</Typography>
      </Box>
    );
  }

  if (!previewData) {
    return null;
  }

  const { project, milestones = [], contractorPayments = [] } = previewData;

  const startChanged =
    project?.originalStartDate &&
    project?.proposedStartDate &&
    !sameCalendarDay(project.originalStartDate, project.proposedStartDate);
  const endChanged =
    project?.originalEndDate &&
    project?.proposedEndDate &&
    !sameCalendarDay(project.originalEndDate, project.proposedEndDate);
  const hasProjectDateChange = Boolean(startChanged || endChanged);

  const netChangeDays = computeProjectNetChangeDays(project);

  const fmt = (d) => (d ? moment(d).format("MMM DD, YYYY") : "—");

  return (
    <Box sx={{ mt: 2, width: "100%" }}>
      {/* Project Timeline — kept as first card section */}
      <Paper elevation={0} sx={impactCardSx}>
        <Box sx={impactCardTitleSx}>
          <Typography sx={impactCardTitleTextSx}>Project Timeline</Typography>
        </Box>
        <Box sx={timelineCardsPaddingSx}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "stretch" }}>
            <TimelineStatCard
              value={fmt(project?.originalStartDate)}
              label="Original Start Date"
            />
            <TimelineStatCard
              value={fmt(project?.originalEndDate)}
              label="Original End Date"
            />

            {hasProjectDateChange ? (
              <>
                <TimelineStatCard
                  value={fmt(project?.proposedStartDate)}
                  label="New Start Date"
                  valueColor={startChanged ? "#1a73e8" : "#0d0d0e"}
                />
                <TimelineStatCard
                  value={fmt(project?.proposedEndDate)}
                  label="New End Date"
                  valueColor={endChanged ? "#1a73e8" : "#0d0d0e"}
                />
                <TimelineStatCard
                  value={formatNetChangeLabel(netChangeDays)}
                  label="Net change"
                  valueColor="#d48806"
                  paperSx={{
                    backgroundColor: "#fffbe6",
                    borderColor: "#ffe58f",
                  }}
                />
              </>
            ) : (
              <TimelineStatCard
                value={formatNetChangeLabel(0)}
                label="Net change"
                valueColor="#389e0d"
                paperSx={{
                  backgroundColor: "#f6ffed",
                  borderColor: "#b7eb8f",
                }}
              />
            )}
          </Box>
        </Box>
      </Paper>

      {/* Milestones — same card + table pattern as mock */}
      <Paper elevation={0} sx={impactCardSx}>
        <Box sx={impactCardTitleSx}>
          <Typography sx={impactCardTitleTextSx}>Milestones</Typography>
        </Box>
        <TableContainer component={Paper} sx={impactTablePaperSx}>
          <Table size="medium">
            <TableHead>
              <TableRow sx={impactTableHeadRowSx}>
                <TableCell>Name</TableCell>
                <TableCell>Original Start Date</TableCell>
                <TableCell>Original End Date</TableCell>
                <TableCell>New Start Date</TableCell>
                <TableCell>New End Date</TableCell>
                <TableCell>Tags</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {milestones.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ ...impactTableBodyCellSx, py: 4, color: "#949DAA" }}>
                    No milestones affected.
                  </TableCell>
                </TableRow>
              ) : (
                milestones.map((m) => {
                  const isStartChanged =
                    m.originalStartDate &&
                    m.proposedStartDate &&
                    !sameCalendarDay(m.originalStartDate, m.proposedStartDate);
                  const isEndChanged =
                    m.originalEndDate &&
                    m.proposedEndDate &&
                    !sameCalendarDay(m.originalEndDate, m.proposedEndDate);

                  return (
                    <TableRow key={m.id} hover>
                      <TableCell sx={{ ...impactTableBodyCellSx, fontWeight: 600 }}>{m.milestoneName}</TableCell>
                      <TableCell sx={impactTableBodyCellSx}>{fmt(m.originalStartDate)}</TableCell>
                      <TableCell sx={impactTableBodyCellSx}>{fmt(m.originalEndDate)}</TableCell>
                      <TableCell
                        sx={{
                          ...impactTableBodyCellSx,
                          fontWeight: isStartChanged ? 600 : 400,
                          color: isStartChanged ? "#1a73e8" : "#0d0d0e",
                        }}
                      >
                        {fmt(m.proposedStartDate)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...impactTableBodyCellSx,
                          fontWeight: isEndChanged ? 600 : 400,
                          color: isEndChanged ? "#1a73e8" : "#0d0d0e",
                        }}
                      >
                        {fmt(m.proposedEndDate)}
                      </TableCell>
                      <TableCell sx={impactTableBodyCellSx}>
                        <Box display="flex" alignItems="center" gap="8px">
                          {m.tags && (
                            <Box display="flex" alignItems="center" sx={{ color: "#949DAA" }}>
                              {m.tags === "Design" ? <NewDesignIcon /> : <NewRenovationIcon />}
                            </Box>
                          )}
                          <Typography sx={{ fontSize: "14px", color: "#0d0d0e" }}>{m.tags || "—"}</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Contractor Payments — identical table treatment */}
      <Paper elevation={0} sx={{ ...impactCardSx, mb: 0 }}>
        <Box sx={impactCardTitleSx}>
          <Typography sx={impactCardTitleTextSx}>Contractor Payments</Typography>
        </Box>
        <TableContainer component={Paper} sx={impactTablePaperSx}>
          <Table size="medium">
            <TableHead>
              <TableRow sx={impactTableHeadRowSx}>
                <TableCell>Group Name</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Original Due Date</TableCell>
                <TableCell>New Due Date</TableCell>
                <TableCell>Impact</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contractorPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ ...impactTableBodyCellSx, py: 4, color: "#949DAA" }}>
                    No contractor payments affected.
                  </TableCell>
                </TableRow>
              ) : (
                contractorPayments.map((cp) => {
                  const isDateChanged =
                    cp.originalDueDate &&
                    cp.proposedDueDate &&
                    !sameCalendarDay(cp.originalDueDate, cp.proposedDueDate);
                  let paymentDiffDays = 0;
                  if (cp.originalDueDate && cp.proposedDueDate) {
                    paymentDiffDays = moment(cp.proposedDueDate)
                      .startOf("day")
                      .diff(moment(cp.originalDueDate).startOf("day"), "days");
                  }
                  const impactText = formatPaymentImpactDays(paymentDiffDays);

                  return (
                    <TableRow key={cp.groupId} hover>
                      <TableCell sx={{ ...impactTableBodyCellSx, fontWeight: 600 }}>{cp.groupName}</TableCell>
                      <TableCell sx={impactTableBodyCellSx}>
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                          <MwebDhiramIcon />
                          {cp.amount != null ? Number(cp.amount).toLocaleString() : "0"}
                        </Box>
                      </TableCell>
                      <TableCell sx={impactTableBodyCellSx}>{fmt(cp.originalDueDate)}</TableCell>
                      <TableCell
                        sx={{
                          ...impactTableBodyCellSx,
                          fontWeight: isDateChanged ? 600 : 400,
                          color: isDateChanged ? "#1a73e8" : "#0d0d0e",
                        }}
                      >
                        {fmt(cp.proposedDueDate)}
                      </TableCell>
                      <TableCell sx={impactTableBodyCellSx}>{impactText}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TimelineImpactReviewCR;
